import { BOOSTER, CREATURE_PRICES, DAILY_ECONOMY, FIRST_CLEAR_STARDUST, PUZZLE_CLEAR_FLOOR, PUZZLE_CLEAR_STARDUST, SHOP_WEIGHTS } from '../../shared/economy'

/**
 * Central balance knobs (gamedesign.md §25). Playtest-tunable values live
 * here, not inside game logic.
 *
 * 🏦 v4.92: every STARDUST amount is imported from shared/economy.ts —
 * the wallet server computes payouts and prices from the same module, so
 * a knob turned here (via there) can never drift from what the ledger
 * actually pays. Tune stardust in shared/economy.ts, everything else here.
 */
export const TUNING = {
  player: {
    startingHp: 45,
    /** 💰 v4.13 (per Javi): runs open with a purse — A5+ cuts it to 20
     * (ascensionStartingGold). */
    startingGold: 45,
  },
  draft: {
    rosterSize: 3,
    startingMaxRosterSize: 3,
    /** Random creatures offered at the draft; the player picks 3.
     * v2.75 (per Javi): 9 → 8. The Wilds lane stays a fixed size no
     * matter how big the owned collection gets.
     * v5.39 (per Javi): 8 → 6. */
    poolSize: 6,
  },
  bonuses: {
    trainingManualBonusXp: 1,
  },
  injury: {
    /**
     * Injuries are disabled for now — earmarked as a future ascension
     * modifier. All injury plumbing stays; flip this to bring it back.
     */
    enabled: false,
    /** 🤕 v5.41 (per Javi: "injury should be −10% to all stats… so it
     * plays a role in higher playthroughs"): each stack multiplies every
     * element by (1 − pct), result floored — a flat −1 was noise once
     * statlines hit the twenties. */
    statPenaltyPct: 0.1,
  },
  acts: {
    /** The run spans this many maps; the last boss wins the run. */
    count: 3,
    /** HP restored when advancing to the next act. */
    healOnAdvance: 10,
    /**
     * Per-act mission scaling (index = act - 1): requirement/damage/XP
     * multipliers and extra requirement elements (§25 difficulty scaling),
     * plus the act PRESSURE ramp — player power compounds across a run
     * (roster size, levels, relics), so later acts must demand more:
     * `goalBonus` adds required successes per combat node (boss exempt),
     * `spawnMult` shortens the spawn cadence, `expireMult` tightens
     * expire timers.
     */
    scaling: [
      // v3.20 (per Javi): damage trimmed slightly across the board —
      // act 1 −5%, act 2 a bit more (−10%), act 3 a touch more (−13%).
      // Requirements untouched; this is a damage-only easing.
      // v4.58 (per Javi): base REQUIREMENTS up ~8% across all three
      // acts (1 → 1.08, 2.3 → 2.5, 3.9 → 4.2) — the whole game leans a
      // little harder. Damage untouched. v4.80 (per Javi: "slightly
      // increase the wall requirements of the whole game"): another
      // ~+7% (1.15 / 2.7 / 4.5), and this time the finalReqBands move
      // with it — v4.58 left the band ceilings put, which quietly
      // clamped part of that bump away.
      { reqMult: 1.15, damageMult: 0.95, xpMult: 1, extraElements: 0, goalBonus: 0, spawnMult: 1, expireMult: 1 },
      // v2.25 (per Javi): act 2 up SLIGHTLY (req/damage ~+5%, expire a
      // touch tighter), act 3 up more (~+8% req/damage, faster spawns,
      // tighter fuses).
      // v4.88 (per Javi: "further acts need to scale the damage
      // more" — the row ramp is right, the act baseline was soft):
      // act 2 damage 1.8 → 1.9, act 3 2.75 → 3.0. Act 1 untouched.
      { reqMult: 2.7, damageMult: 1.9, xpMult: 1.4, extraElements: 1, goalBonus: 1, spawnMult: 0.8, expireMult: 0.86 },
      { reqMult: 4.5, damageMult: 3.0, xpMult: 1.8, extraElements: 1, goalBonus: 2, spawnMult: 0.66, expireMult: 0.72 },
    ],
    /**
     * Within-act ROW ramp: each act opens at its baseline difficulty and
     * climbs row by row (requirement/damage multipliers per local row;
     * applied after the act band clamp; bosses exempt). Old saves with
     * 7-row acts get a slightly offset ramp — harmless.
     */
    // v3.34 (per Javi): requirements climb the rows a bit harder
    // (0.04 → 0.06/row), and a per-row damage FLOOR keeps deep-row
    // "pokes" honest — a row-7 easy mission can't roll a 2 anymore
    // (floor = 1 + round(row × damageFloorPerRow); act 1 row 7 → 5).
    // v3.35 (per Javi's act-8 screenshot): damagePerRow doubled
    // (0.05 → 0.10) — a row-7 mission hits +70% over the act baseline.
    rowRamp: { reqPerRow: 0.06, damagePerRow: 0.1, damageFloorPerRow: 0.6 },
    /**
     * 🪚 v7.21 (per Javi: "damage is a saw tooth pattern, with higher
     * highs but the same lows"): sampled from the generator, it was.
     * Peaks climbed 28 / 40 / 62 / 75 / 90 across acts 1-5 while the
     * lowest roll at each act's door sat at 1 / 2 / 4 / 5 / 5 - the act
     * baseline, the door grace, an off-focus ease and a near site stack
     * up to act-1 numbers deep into a run. A second FLOOR lifts the
     * troughs without touching the peaks: no regular mission rolls below
     * this fraction of the act's own easy-tier minimum at its row (danger
     * 1, no eases). At 1.0 the door minimums become 2 / 4 / 6 / 7 / 9;
     * medians and ceilings do not move. The v3.34 per-row floor still
     * applies wherever it is the higher of the two (act 1's deep rows).
     */
    damageFloorOfEasyMin: 1,
    /**
     * 🚪 Act-entry grace (v5.09, per Javi: "the beginning of an act
     * feels more difficult than the later rows of the previous act" —
     * verified: act 2's door stepped walls ×2.0 and damage ×1.33 over
     * act 1's final row; every gauntlet door stepped walls ×1.42).
     * The first two LOCAL rows of an act ramp IN from the previous
     * act's ceiling instead: post-band multipliers by local row
     * (index 0 and 1; anything past the array = 1). Bosses exempt, and
     * rows 2+ are untouched — end-of-act and boss difficulty are
     * exactly what they were; only the door is reshaped.
     */
    actEntryEase: {
      act2: { req: [0.62, 0.82], damage: [0.75, 0.88] },
      act3: { req: [0.85, 0.93], damage: [1, 1] },
      /** Applies at EVERY appended gauntlet act's door (act 4+). */
      gauntlet: { req: [0.75, 0.88], damage: [1, 1] },
    },
    /**
     * ⚔️ Gauntlet mode (v2.34, per Javi): acts past act 3 compound the
     * REQUIREMENT ramp by this factor per act. "Brutal — few survive
     * act 5" was the approved feel.
     */
    // v4.58 (per Javi: "gauntlet should ramp even more after act 3"):
    // 1.375 → 1.5. v4.74 (per Javi: "a slight bump in [the amount
    // required]"): 1.5 → 1.6 — this factor applies exactly ONCE for
    // any gauntlet act, so it's a flat +6.7% on every wall past act 3
    // without steepening the compounding slope.
    gauntletRampPerAct: 1.6,
    /**
     * ⚔️ v4.38 (per Javi: "ramp up even more starting after act 4 — I
     * had a game where I was able to go to act 27, took 5 hours;
     * ideally the perfect set up gets to act 12 ish"): the ramp's
     * SECOND STAGE. Act 4 compounds at the classic 1.375; from act 5
     * every further act compounds at this steeper rate instead. First
     * calibration — tune this one knob if 12 still isn't the wall.
     */
    // v4.58: 1.6 → 1.75 (with the base bump, act 12 demands ~2.2× the
    // old wall — the perfect setup should stall around act 10-11 now).
    // v4.80 (per Javi: "increase wall requirement scaling after act 4
    // in gauntlet mode"): 1.75 → 1.85 — act 12 asks ~1.55× v4.74's wall.
    // v5.52 (per Javi: "more exponential… so the wall past act 12 is
    // really really hard"): 1.85 → 1.95, and a THIRD stage below.
    gauntletLateRampPerAct: 1.95,
    /** Extra acts (past act 3) that keep the classic rate — 1 = act 4. */
    gauntletLateRampFromExtra: 1,
    /** ⚔️ v5.52: the WALL stage — every act past act 12 compounds at
     * this brutal rate on top of the two stages above. Past-12 is a
     * victory lap that ends fast, by design. */
    gauntletWallRampPerAct: 2.5,
    /** Extra acts (past act 3) before the wall stage bites — 9 = the
     * wall starts compounding from act 13. */
    gauntletWallRampFromExtra: 9,
    /**
     * v3.35 (per Javi's act-8 screenshot: 100-point requirements poking
     * for 6): damage now ramps through the gauntlet too — gentler than
     * the requirement ramp. Bosses exempt: their chip already scales
     * with their ramped walls via missing coverage. XP stays at act-3
     * level.
     */
    // v4.73 (per Javi): 1.15 → 1.2 — act 5 ≈ ×1.44, act 8 ≈ ×2.49,
    // act 12 ≈ ×5.2.
    gauntletDamageRampPerAct: 1.2,
    /**
     * ⚔️ PARKED at 0 (v4.74): the v4.73 "+1 distinct element in
     * gauntlet" read Javi's "elements required" as VARIETY — he meant
     * the requirement TOTAL (the wall). "I like where [the variety]
     * sits." The machinery stays: raise this to widen gauntlet spreads
     * (easy 3, medium 3-4, hard 4-5; 5-cap applies; bosses exempt).
     */
    gauntletExtraElements: 0,
  },
  map: {
    /** Total rows including the boss row at the top. */
    rows: 8,
    /** Paths are 3 wide, with some rows fanning out to 4 (per Javi). */
    minNodesPerRow: 3,
    maxNodesPerRow: 4,
    /** Elites don't appear before this row. */
    eliteMinRow: 2,
    /**
     * Exactly this many elite NODES per act, placed on mid combat rows
     * so every act always has both an elite path and an elite-free path
     * (siblings in the elite's row stay elite-free). Raising this is a
     * future ascension modifier.
     */
    elitesPerAct: 1,
    /** Shop NODES per act, placed exactly like elites (siblings stay
     * shop-free, so a shop path and a shop-free path always exist). */
    shopsPerAct: 1,
    /** 🗿 v5.42 (per Javi): titans return as SHORTCUT nodes — one per
     * act, sibling-guaranteed like elites, and its exit edges skip the
     * next row entirely: a boss-lite wall worth two rows of progress. */
    titansPerAct: 1,
    /** Extra mid-act rest NODES per act, placed shop-style on rows 2+
     * BEFORE the chest row (per Javi: a breather on one of the paths in
     * front of the relic reward, never in the first two rows). */
    restStopsPerAct: 1,
    /**
     * Combat weights for normal rows. Rest, chest, mystery, and elite
     * placement follow the path-balance rules instead of these weights.
     * Mixed nodes are temporarily disabled for playtesting (their blends
     * strayed from the stated threats); swarm is the workhorse.
     */
    // titan retired (v1.79, per Javi — too close to elite); type stays for old saves.
    typeWeights: { swarm: 5, puzzle: 2 },
    /**
     * Path balance: every root-to-boss path crosses each row once, so one
     * all-chest row = exactly one reward node per path, and an all-rest
     * row before the boss = a guaranteed rest on every path.
     */
    /** [4, 4] pins the chest row to the 5th row — one extra combat row
     * now sits in front of the relic reward (v0.45). */
    chestRowRange: [4, 4],
    /** Mystery nodes GUARANTEED per act over combat rows; [min, max]. */
    mysteryCount: [3, 5],
    /** Chance a mystery node turns hostile / treasure / respite / puzzle
     * — plus, since v5.39 (designed with Javi), a narrative EVENT drawn
     * from the good or bad deck (app/data/events.ts). Shares sum to 1. */
    mysteryOutcomes: { ambush: 0.25, treasure: 0.2, respite: 0.15, puzzle: 0.14, goodEvent: 0.13, badEvent: 0.13 },
    /** [min, max] successful missions required, per combat node type.
     * Lowered in the capacity rebalance (sim: inflow exceeded what a
     * 3-creature roster could serve once round trips were counted). */
    /** Swarm goals dropped to [2,3] in v0.46: the swarm identity lives in
     * the flooded field (spawn pace + small missions), not the goal —
     * lower goals keep 8-row acts from overstaying their welcome. */
    requiredSuccesses: {
      swarm: [3, 3],
      mixed: [2, 3],
      titan: [2, 2],
      elite: [2, 3],
      ambush: [1, 2],
      boss: [1, 1],
      /** Nominal for previews; the real goal = generated mission count. */
      puzzle: [3, 3],
    },
    /** Rows 0..easyMaxRow are easy, up to mediumMaxRow medium, then hard. */
    easyMaxRow: 1,
    mediumMaxRow: 3,
  },
  missions: {
    /** 🧱 v5.39 (per Javi: "slightly increase the wall requirement for
     * the whole game"): a global multiplier on every mission's required
     * total. Applied AFTER the band clamp (like ascension pressure), so
     * the ceilings can't quietly swallow it — the v4.58/v4.80 lesson.
     * v5.66 (per Javi: "the game feels a little too hard" with titans,
     * modifiers and the affliction curve stacked since): back to 1. */
    globalReqMult: 1,
    /** 🗿 v5.42: titan missions are the WALL — bigger asks, richer XP
     * (the node is worth two rows of progress; see map.titansPerAct). */
    titanReqMult: 1.3,
    titanXpMult: 1.25,
    /**
     * Per-tier mission generation ranges (gamedesign.md §25). All [min, max].
     * Timers are seconds.
     */
    tiers: {
      // Fight bands trimmed ~25% in the v2.08 pacing pass — travel got
      // longer (travel.timeMult/minSeconds), fights pay for it.
      easy: { totalReq: [3, 5], elements: [1, 1], slots: [1, 2], damage: [2, 4], xp: [2, 3], expire: [38, 46], fight: [4, 7] },
      medium: { totalReq: [6, 10], elements: [1, 2], slots: [2, 3], damage: [5, 8], xp: [4, 6], expire: [46, 55], fight: [7, 10] },
      hard: { totalReq: [11, 16], elements: [2, 3], slots: [3, 5], damage: [9, 14], xp: [7, 10], expire: [52, 62], fight: [10, 13] },
      // Boss damage nerfed v2.26 ([15,18] → [11,14]), then raised again
      // v3.34 (per Javi: "boss requirements are scaling perfectly but
      // damage needs to go up") — [11,14] → [14,17], most of the v2.26
      // cut given back now that the win-chip mercy floor exists.
      boss: { totalReq: [18, 22], elements: [3, 3], slots: [4, 5], damage: [14, 17], xp: [12, 15], expire: [66, 82], fight: [13, 17] },
    },
    /** Fallback focus adherence when no per-act value applies. */
    focusBias: 0.9,
    /** Per-act focus adherence (index = act − 1): act 1 honors the
     * posted threats tightly so the draft plan works; later acts get
     * wilder (per Javi, 2026-07-05). Off-focus rolls stay eased. */
    focusBiasByAct: [0.9, 0.8, 0.7],
    /** 🎯 v7.29 (player testing, per Javi): on an act-1 node posting two
     * threats, a would-be single-element mission posts BOTH threats this
     * often. Act 1 was 88% single-element and act 2 0% - a cliff; 0.35
     * puts swarm nodes near 55% single. The opening wave covers both
     * threats regardless (missiongen, spawn index 0 and 1). */
    act1SplitChance: 0.35,
    /** Missions that roll ANY off-focus element get this multiplier on
     * their total requirement and damage — surprises stay beatable
     * speed bumps instead of hard counters. */
    offFocusEase: 0.7,
    /** Success chance = coverage^exp × 100 (before coverage relics).
     * 1 = linear. 0.65 softens near-misses — being 1 point short on a
     * small mission is ~77% instead of 67% — while 100% stays certain
     * and 0% stays impossible. Shipped after sim experiment F. */
    successCurveExp: 0.65,
    /** v3.33 (per Javi): the forgiveness FADES by act — 0.65 is an act-1
     * training wheel, act 2 keeps a sliver, act 3+ rolls honest linear
     * coverage. Index = act − 1, clamped to the last entry for gauntlet
     * acts past 3. */
    successCurveExpByAct: [0.65, 0.9, 1],
    /**
     * Act-aware FINAL requirement bands: after the tier roll × act
     * scaling × distance danger, the total is clamped here and rounded
     * once — a late-act mission can never roll trivially low, and no
     * roll spikes absurdly high (audit: 65% of act-3 easy missions used
     * to roll below 10 total).
     */
    finalReqBands: {
      // v4.80: bands scaled ~+7% alongside reqMult — a raised
      // multiplier is a no-op wherever the old ceilings clamp.
      act1: { easy: [3, 6], medium: [6, 11], hard: [11, 16], boss: [17, 24] },
      // Bands track the v2.25 scaling bumps for REGULAR missions; the
      // BOSS clamps were rolled back in v2.26 (per Javi — act bosses
      // were already right): act 2 boss to its pre-v2.25 band, act 3
      // boss slightly BELOW it.
      act2: { easy: [13, 18], medium: [18, 26], hard: [26, 36], boss: [33, 44] },
      act3: { easy: [18, 27], medium: [28, 39], hard: [39, 54], boss: [47, 60] },
    },
  },
  travel: {
    /** 0 = innate creature speeds (normal). Set >0 to flatten everyone
     * to that pace (used by the v0.55 no-timer experiment). */
    uniformSpeed: 0,
    /**
     * 🏃 Pacing pass (v2.08, per Javi): trips looked too fast for the
     * run animations (a 4s stride cycle vs 2–5s trips). `timeMult`
     * stretches all travel; `minSeconds` floors each leg so every trip
     * plays at least one full stride cycle — it mostly affects the
     * short hops that looked silliest. Fight timers were trimmed ~25%
     * in exchange, keeping round trips (and the sim-balanced expire /
     * capacity math) roughly unchanged.
     */
    timeMult: 1.6,
    minSeconds: 3.5,
    /** Missions land anywhere on the field; their distance from home (in
     * these units) drives travel time (distance / team speed, slowest
     * member) and danger scaling. maxDistance 14 keeps round trips short
     * enough that the roster can keep cycling (capacity rebalance). */
    minDistance: 6,
    maxDistance: 14,
    /** Requirement/damage/XP scaling across the distance range (boss exempt). */
    dangerMin: 0.9,
    dangerMax: 1.25,
  },
  encounters: {
    /** The v0.54–v0.57 no-expiry experiment is OVER (per Javi,
     * 2026-07-08) — timers are back. The machinery stays: set false to
     * re-run the experiment (regular missions stop expiring and the
     * goal-relative cap below takes over; pair with lower startingHp). */
    expiryEnabled: true,
    /** Only used while expiryEnabled is false: field cap = goal + this. */
    capOverGoal: 2,
    /** Pacing and mission mix per combat node type. Swarm pace is the
     * baseline; missions cap at 6 on screen (the boss keeps 1 so
     * colossal missions never stack). The field starts empty:
     * `initialSpawns` is the size of the opening wave, which trickles in
     * one mission at a time instead of appearing all at once. */
    byNodeType: {
      swarm: { tierWeights: { easy: 3, medium: 1 }, spawnEvery: [9, 13], maxActive: 6, expireMult: 0.9, initialSpawns: 2 },
      mixed: { tierWeights: { easy: 2, medium: 2 }, spawnEvery: [9, 13], maxActive: 6, expireMult: 1, initialSpawns: 1 },
      titan: { tierWeights: { medium: 1, hard: 2 }, spawnEvery: [11, 15], maxActive: 6, expireMult: 1.3, initialSpawns: 1 },
      elite: { tierWeights: { medium: 2, hard: 2 }, spawnEvery: [9, 13], maxActive: 6, expireMult: 1, initialSpawns: 1 },
      ambush: { tierWeights: { easy: 1, medium: 2 }, spawnEvery: [9, 13], maxActive: 6, expireMult: 1, initialSpawns: 2 },
      boss: { tierWeights: { boss: 1 }, spawnEvery: [5, 8], maxActive: 1, expireMult: 1, initialSpawns: 1 },
      /** Puzzle fields spawn nothing — all missions appear at once. */
      puzzle: { tierWeights: { medium: 1 }, spawnEvery: [999, 999], maxActive: 0, expireMult: 1, initialSpawns: 0 },
    },
    /**
     * 👑 Chance that a HARD mission on an elite node wears an Elite
     * Champion's name and painted art (v3.80, per Javi: "I want to make
     * them a bit more rare... a chase item in collecting").
     *
     * It used to be 1.0 — every hard elite mission was a champion, and
     * with elite nodes rolling hard at 2-in-4 that put roughly ten of
     * them in a single run. Seeing all eight took no effort at all, which
     * is the opposite of a chase. At 0.25 a run yields two or three, so
     * filling the codex takes a few climbs and a lucky element roll.
     *
     * Nothing about the FIGHT changes — the champion is a name and a
     * portrait on a mission that was already hard.
     */
    // v5.12 (per Javi: "slightly more rare... just the enemy image"):
    // 0.25 → 0.15 — roughly one or two champions per run instead of
    // two or three. The fight underneath is unchanged.
    championChance: 0.15,
    /** Seconds after arriving at a node before the first mission appears. */
    firstSpawnDelay: [2, 3],
    /** Stagger between opening-wave missions (until initialSpawns spawned). */
    openingSpawnEvery: [2, 4],
    /** Expired missions deal this share of their damage (rounded, min 1).
     * Ignoring a mission should sting less than failing it with a team. */
    expiredDamageMult: 0.5,
    /** ⏫ Enrage (v4.72, per Javi: "missions should gradually start
     * dealing more and more damage as time goes on, so infinite farming
     * is not possible" — a Field Medic roster could AFK-camp a node
     * forever). Once armed, every NEWLY SPAWNED mission's damage grows
     * +1%/second, baked in at spawn so the card always shows the real
     * bite. A camper's expiries outgrow any healing loop. */
    // v4.75 (per Javi): the trigger is SPAWN COUNT now, not a fixed
    // 240s grace. v4.76: the seventh spawn — only real combat spawns
    // count (gold, training and minions ride their own counters).
    // v4.84 (per Javi: "push back the enrage by 2"): the NINTH.
    escalationAfterSpawns: 9,
    /** 🎓 v7.41 (per Javi, D7/D8): a FIRST RUN's rails on act-1 rows 0-1
     * (locations 1-2): the enrage arms at the 15th spawn instead of the 9th
     * (it still arms - v4.72's no-farming rule holds; every competent test
     * clear finished by the 11th), and only easy-tier missions spawn, the
     * row-1 ambush included. See utils/firstRunRails.ts. */
    firstRunRails: { rows: 2, escalationAfterSpawns: 15 },
    // v4.74 (per Javi: "make it ramp slightly faster like 1/sec"):
    // 0.25 → 0.6. v4.80 (per Javi: "up the enrage to 5%/sec"): 0.6 →
    // 3.0 — one armed minute quadruples spawn damage. Camping is over.
    escalationPerMinute: 3.0,
    /** Seconds a finished mission card lingers before disappearing. */
    cardLinger: 1.4,
    /** Game clock resolution in milliseconds. */
    tickMs: 100,
  },
  /** 😏 v7.47 (per Javi: "everyone, sparingly"): the monsters' taunts - a
   * short line in a bubble beside the monster (data/taunts.ts, the rules in
   * utils/tauntFloor.ts). The practice's are scripted; a first run's guided
   * location 1 hears up to `firstRunMax`; everyone else at most `perLocation`
   * per location, entrances only. Compiled in: 0 turns them off, in a release. */
  taunts: {
    /** Veterans: taunts per location (a champion's entrance on an elite
     * node, else an ambush's opening monster). 0 = none at all. */
    perLocation: 1,
    /** A first run's location 1, while the guide is on. */
    firstRunMax: 6,
    /** ...and at least this many seconds of PLAY (the location's clock,
     * enc.secondsElapsed) between two of them. */
    gapSeconds: 10,
    /** How long a bubble stays up (ms): an entrance, an escape. The
     * practice's escape lasts its got-away freeze (FIRST_DISPATCH.gotAwayMs). */
    appearMs: 1600,
    awayMs: 2200,
    /** The bubble's fade out and pop in (ms). */
    fadeMs: 200,
    popMs: 320,
  },
  gold: {
    /** Gold missions run on their own clock, independent of combat
     * spawns — one can appear alongside a regular mission. */
    spawnEvery: [14, 24],
    /** At most this many gold missions on the field at once. */
    maxActive: 1,
    /** Gold paid on success, scaled by act via actMult (v0.76: acts 2–3
     * nerfed from the old ×act — payouts were snowballing). Gold
     * missions never count toward the node goal; letting one expire is
     * harmless (no damage) — failing an attempt hurts as usual. */
    rewardBase: [8, 14],
    /** Act payout multiplier (was 1/2/3 via ×act). */
    actMult: [1, 1.7, 2.4],
  },
  training: {
    /** 🎓 Training missions (v0.80, per Javi): the SECOND mission spawn
     * on the act's first regular combat node — predictable and early,
     * once per act (was a mid-node chance roll forced by row 5). */
    /** Seconds after the node's first mission before training arrives. */
    spawnAfterFirst: 2,
    /** Snappier fuse (v0.80, was 90) — commit a student or lose it. */
    expire: 45,
    /** The training itself is a long occupation. */
    fight: 40,
  },
  shop: {
    /** Relics stocked per shop visit. */
    stock: 6,
    /** Price per rarity tier. */
    prices: { Common: 15, Uncommon: 25, Rare: 40, Epic: 60, Legendary: 90 },
    /** v0.84 (per Javi): every shop ALWAYS offers a 🎓 Training Ground
     * and a 🐾 New Companion at this flat price (Haggler halves it). */
    specialPrice: 120,
  },
  creatures: {
    /**
     * 🌟 Shop price PER RARITY (v3.62, per Javi's economy pass). Was a
     * flat 1 while playtesting — which made every collection free, and
     * is why these numbers only mean anything alongside a reset.
     *
     * Anchored to the earn rate: a 3-act run pays 70 ⭐ at A0 and triple
     * that at A10, so an uncommon is a couple of runs, a rare an evening,
     * an epic a week of evenings, and a legendary a genuine goal that
     * ascension play cuts to a third. Permanent ownership.
     */
    // v3.75 (per Javi, +25%): the Puzzle Game's first-clear payout once
    // outpaid the entire ascension first-clear career; v3.76 and v5.16
    // pulled one-time income back down (puzzles 1,930, ascension 2,800 —
    // see shared/economy.ts, the single source of truth).
    prices: CREATURE_PRICES,
    /** How many creatures the shop stocks at a time (v2.75). */
    shopStock: 5,
    /**
     * 🎴 Booster pack (v3.20; repriced v3.62): four random rarity-weighted
     * creatures. At the real pull odds a pack averages ~983 ⭐ of shelf
     * value when every card is new, so 550 stays a clear win early and
     * decays on its own as the collection fills — which is the shape we
     * want, packs to BUILD and the shelf to TARGET.
     *
     * 700 since v3.75, tracking the +25% shelf bump: first-clear rewards
     * inject a one-time 2,800 ⭐ over a player's history and the Puzzle
     * Game a further 1,930 (halved v5.16 — see `puzzleClearStardust`).
     *
     * Each DUPLICATE refunds a share of ITS OWN rarity's price — and the
     * SHARE climbs with rarity too (v4.34, per Javi: "if you get a
     * legendary refund, it should be around two booster packs worth").
     * Legendary's 0.56 × 2,500 = 1,400 ⭐ = exactly two packs; a dupe
     * uncommon still stings. Rungs: 53 / 152 / 450 / 1,400.
     */
    booster: BOOSTER,
    /**
     * 🏅 First-time clear bounty (v3.62, per Javi; halved v5.16): beating
     * a mode for the FIRST time pays 50 ⭐ per ascension level — Normal
     * and A1 both pay 50, A2 pays 100, up to A10's 500. Once per level,
     * ever (career total 2,800). The Gauntlet has no summit to clear, so
     * it pays nothing.
     */
    firstClearStardust: FIRST_CLEAR_STARDUST,
    /** 🧩 Puzzle Game first-clear payout per level, once per level ever.
     * v3.76: 10 → 3 ⭐ × level — the ladder was outpaying the entire
     * ascension career and pulling the game's centre of gravity onto
     * puzzles. v5.16 halved it with the ascension bounty: 1.5 ⭐ × level
     * (ceil, floor 5), 1,930 across all 50 — still under the 2,800
     * ascension career, same pecking order. */
    puzzleClearStardust: PUZZLE_CLEAR_STARDUST,
    /** Floor so the early levels still pay something you notice. */
    puzzleClearFloor: PUZZLE_CLEAR_FLOOR,
    /** The shelf re-rolls on this bucket — TWELVE hours since v3.77 (per
     * Javi; was one). Stock is derived from the bucket index, so it is
     * stable across reloads and cannot be re-rolled by refreshing.
     *
     * Twelve divides the day evenly, which matters: the bucket is
     * `floor(epochMs / refreshMs)`, so the turnover lands at the same two
     * clock times every day (00:00 and 12:00 UTC) rather than drifting.
     * An interval that does not divide 24h would walk around the clock
     * and make "when does it restock" unanswerable. */
    shopRefreshMs: 43_200_000,
    /** Draft roll weights by creature rarity: the eight random options
     * mostly roll baseline creatures, with the odd rare as a lucky
     * tease (owned or not — purchases make them ALWAYS available in
     * their own draft section instead). v2.75 (per Javi): rare 15 → 18
     * to hold today's ~74% per-run rare exposure at 8 options, plus
     * epic/legendary weights ready for the expansion — an epic about
     * 1 run in 4, a legendary about 1 in 22. */
    draftWeights: { uncommon: 100, rare: 18, epic: 7, legendary: 3 },
    /** The shelf rolls FLATTER than the draft (v2.75): the draft is a
     * lottery, the shop is deliberate acquisition, so the premium tiers
     * have to actually be visible on it. Uncommons stay in the mix per
     * Javi. At 24 uncommons / 10 rares this puts a rare on roughly half
     * of all shelves instead of one in three. */
    shopWeights: SHOP_WEIGHTS,
    /** v2.75: the Wilds lane decays as the collection fills — a veteran
     * rolls eight creatures they already own and the lane stops meaning
     * anything. Creatures the player does NOT own roll at this
     * multiplier, which keeps ~2 of the 8 unseen at 60 owned. */
    unownedDraftBias: 3,
  },
  skills: {
    /** Elemental Surge: +N to the creature's main element, next mission. */
    surgeBonus: 2,
    /** Dual Surge: +N to EACH tied main element, next mission. */
    dualSurgeBonus: 1,
    /** 🌈 Prism Surge (v2.28): the trained upgrade — +N to EVERY element. */
    omniSurgeBonus: 3,
    /** Seconds after the creature returns home before it can surge
     * again (9999 = once per node, used by the v0.55 experiment). */
    cooldownSeconds: 30,
    /** 🧘/📖/🔥 Activated trained-skill cooldown (v1.12, per Javi):
     * Equilibrium flips between builds on this clock, and consumed
     * Tough Learner / Pain Into Power charges wait it out to re-arm. */
    trainedCooldownSeconds: 30,
  },
  rewards: {
    /** Cards offered per reward stage; per-reward weights live on the defs. */
    optionCount: 3,
    /** Absolute roster cap; New Companion stops appearing at this size. */
    maxRosterSize: 8,
  },
  xp: {
    /** Share of a mission's XP granted (×coverage) when it FAILS.
     * 0 since v0.29 (per Javi, ahead of creature skills): failures teach
     * nothing — relics like Shared Lessons still pay their flat bonus. */
    failureXpMult: 0,
    /**
     * XP needed to go from `level` to `level + 1`.
     * Doc curve 5, 7, 10 continued with a step that grows by one each
     * level: 14, 19, 25, ... (closed form of that sequence).
     */
    toNextLevel(level: number): number {
      return 4 + (level * (level + 1)) / 2
    },
  },

  /** 📅 The Daily Silhouette (v4.03). Flat trickles by design — Javi's
   * rule is that ascension/gauntlet farming stays the economic drive, so
   * a perfect daily (~15✨) is worth less than one weak act clear, and
   * the streak ladder is retention, not income. The 30-day booster is
   * the ceremony at the top; every further 30 pays another. */
  daily: DAILY_ECONOMY,
} as const
